"use client";

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL } from "@ffmpeg/util";
import logger from "@/lib/logger";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";

// Lazy ffprobe-wasm loader
let FFprobeWorker: any = null;
let ffprobeLoadPromise: Promise<void> | null = null;

const ensureFFprobeLoaded = async () => {
	if (FFprobeWorker) return;
	if (typeof window === "undefined") return;
	if (!ffprobeLoadPromise) {
		ffprobeLoadPromise = import("ffprobe-wasm")
			.then((module: any) => {
				FFprobeWorker = module.FFprobeWorker;
			})
			.catch((error: any) => {
				logger.warn({ error }, "Failed to load ffprobe-wasm");
			});
	}
	await ffprobeLoadPromise;
};

interface FFmpegContextValue {
	ffmpegInstance: FFmpeg | null;
	isLoaded: boolean;
	isLoading: boolean;
	hardwareInfo: HardwareInfo;
	error: string | null;
	loadFFmpeg: () => Promise<void>;
	// Primary: ffprobe-wasm functionality
	analyzeWithFFprobe: (file: File) => Promise<any>;
	// Fallback: Metadata extraction using FFmpeg
	extractMetadata: (inputFile: string) => Promise<any>;
	// Combined method that tries ffprobe-wasm first, then falls back
	getFileMetadata: (file: File, inputFileName?: string) => Promise<any>;
	// Frame-level analysis using ffprobe-wasm
	getFrameAnalysis: (file: File) => Promise<any>;
}

interface HardwareInfo {
	cores: number;
	memory: number;
	isUltraHighEnd: boolean;
	isHighEnd: boolean;
	isMidRange: boolean;
	isLowEnd: boolean;
	supportsSharedArrayBuffer: boolean;
	balancedThreads: number;
	aggressiveThreads: number;
	recommendedPreset: string;
	detectedArchitecture: string;
}

const defaultHardwareInfo: HardwareInfo = {
	cores: 4,
	memory: 4,
	isUltraHighEnd: false,
	isHighEnd: false,
	isMidRange: true,
	isLowEnd: false,
	balancedThreads: 2,
	aggressiveThreads: 4,
	recommendedPreset: "fast",
	supportsSharedArrayBuffer: false,
	detectedArchitecture: 'x86_64',
};

const FFmpegContext = createContext<FFmpegContextValue | null>(null);

export const useFFmpeg = () => {
	const context = useContext(FFmpegContext);
	if (!context) {
		throw new Error("useFFmpeg must be used within an FFmpegProvider");
	}
	return context;
};

const detectHardwareCapabilities = async (): Promise<HardwareInfo> => {
	// Return safe defaults during SSR
	if (typeof window === "undefined") {
		return { ...defaultHardwareInfo };
	}

	// Detect system architecture with enhanced methods
	const detectArchitecture = async (): Promise<string> => {
		const ua = navigator.userAgent.toLowerCase();

		// Method 1: Check for Apple Silicon indicators in user agent
		if (ua.includes('macintosh') || ua.includes('macintel')) {
			if (ua.includes('arm64') || ua.includes('aarch64')) {
				return 'arm64';
			}

			// Method 2: Advanced WebGL-based Apple Silicon detection
			try {
				const canvas = document.createElement('canvas');
				const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
				if (gl) {
					const glContext = gl as WebGLRenderingContext;
					const renderer = glContext.getParameter(glContext.RENDERER);

					logger.debug({ renderer }, "WebGL detection info");

					if (renderer) {
						const rendererStr = renderer.toLowerCase();
						if (rendererStr.includes('apple m1') || rendererStr.includes('apple m2') ||
						    rendererStr.includes('apple m3') || rendererStr.includes('apple m4') ||
						    rendererStr.includes('apple gpu') || rendererStr.includes('apple silicon')) {
							return 'arm64';
						}

						if (rendererStr.includes('apple') && (
							rendererStr.includes('gpu') ||
							rendererStr.includes('metal') ||
							rendererStr.includes('a14') || rendererStr.includes('a15') ||
							rendererStr.includes('a16') || rendererStr.includes('a17')
						)) {
							return 'arm64';
						}
					}
				}
			} catch (error) {
				logger.debug({ error }, "WebGL detection failed");
			}

			// Method 3: macOS version-based detection with Apple Silicon timeline
			const macOSVersion = ua.match(/mac os x (\d+)[._](\d+)/);
			if (macOSVersion) {
				const major = parseInt(macOSVersion[1]);
				const minor = parseInt(macOSVersion[2]);

				logger.debug({ major, minor }, "Detected macOS version");

				if (major >= 12) {
					const cores = navigator.hardwareConcurrency || 4;
					const memory = (navigator as any).deviceMemory || 8;

					if (cores >= 8) {
						return 'arm64';
					}

					if (cores >= 4 && memory >= 8) {
						return 'arm64';
					}
				} else if (major === 11) {
					const cores = navigator.hardwareConcurrency || 4;
					const memory = (navigator as any).deviceMemory || 8;

					if (cores >= 8 && memory >= 16) {
						return 'arm64';
					}
				}
			}

			// Method 4: Performance-based heuristics for Apple Silicon
			try {
				const cores = navigator.hardwareConcurrency || 4;

				if (cores >= 10) {
					return 'arm64';
				}

				if (cores >= 8 && typeof WebAssembly !== 'undefined') {
					const start = performance.now();

					let result = 0;
					for (let i = 0; i < 100000; i++) {
						result += Math.sqrt(i) * Math.sin(i);
					}
					logger.debug({ result: result > 0 ? 'computed' : 'failed' }, "Performance test result (ignore)");

					const duration = performance.now() - start;

					if (duration < 10 && cores >= 8) {
						return 'arm64';
					}
				}
			} catch {
				// Performance test failed, continue
			}

			// Method 5: Screen resolution and device characteristics
			try {
				const screen = window.screen;
				const devicePixelRatio = window.devicePixelRatio || 1;

				const width = screen.width * devicePixelRatio;
				const height = screen.height * devicePixelRatio;

				if ((width === 3024 && height === 1964) ||
				    (width === 3456 && height === 2234) ||
				    (width === 2880 && height === 1864) ||
				    (width === 3024 && height === 1890)) {

					const cores = navigator.hardwareConcurrency || 4;
					if (cores >= 8) {
						return 'arm64';
					}
				}
			} catch {
				// Screen detection failed
			}
		}

		// Method 6: Use userAgentData API when available (modern browsers)
		const userAgentData = (navigator as any).userAgentData;
		if (userAgentData) {
			try {
				const highEntropyValues = await userAgentData.getHighEntropyValues(['architecture', 'platform', 'platformVersion']);
				if (highEntropyValues.architecture) {
					const arch = highEntropyValues.architecture.toLowerCase();
					if (arch.includes('arm') || arch.includes('aarch64')) {
						return 'arm64';
					}
					if (arch.includes('x86_64') || arch.includes('x64')) {
						return 'x86_64';
					}
				}
			} catch {
				// High entropy values not available, continue with other methods
			}

			if (userAgentData.platform) {
				const platform = userAgentData.platform.toLowerCase();
				if (platform.includes('mac') && ua.includes('arm')) {
					return 'arm64';
				}
			}
		}

		// Fallback to deprecated platform property
		const platform = navigator.platform?.toLowerCase() || '';

		if (ua.includes('mac') && (ua.includes('arm') || platform.includes('arm'))) {
			return 'arm64';
		}

		if (platform.includes('mac') || platform.includes('win64') || platform.includes('linux x86_64')) {
			return 'x86_64';
		}

		if (platform.includes('win32') || platform.includes('linux i686')) {
			return 'x86_32';
		}

		return 'x86_64';
	};

	let architecture = await detectArchitecture();

	// Check for manual architecture override (for debugging)
	const urlParams = new URLSearchParams(window.location.search);
	const manualArch = urlParams.get('arch');
	if (manualArch && (manualArch === 'arm64' || manualArch === 'x86_64')) {
		logger.debug({ manualArch }, "Manual architecture override detected");
		architecture = manualArch;
	}

	// Log environment information for debugging
	logger.debug({
		userAgent: navigator.userAgent,
		platform: navigator.platform,
		detectedArchitecture: architecture,
		hardwareConcurrency: navigator.hardwareConcurrency,
		deviceMemory: (navigator as any).deviceMemory,
		screenWidth: window.screen.width,
		screenHeight: window.screen.height,
		devicePixelRatio: window.devicePixelRatio,
		actualScreenWidth: window.screen.width * (window.devicePixelRatio || 1),
		actualScreenHeight: window.screen.height * (window.devicePixelRatio || 1),
		isSecureContext: window.isSecureContext,
		location: window.location.href,
		protocol: window.location.protocol,
		hasSharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
		crossOriginIsolated: window.crossOriginIsolated,
		manualOverride: manualArch || 'none',
	}, "Environment detection");

	// Check for SharedArrayBuffer support with deployment-aware fallback
	const hasSharedArrayBuffer = typeof SharedArrayBuffer !== "undefined";
	const isCrossOriginIsolated = window.crossOriginIsolated;
	const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

	const supportsSharedArrayBuffer = hasSharedArrayBuffer && isCrossOriginIsolated && (!isProduction || window.isSecureContext);

	const cores = navigator.hardwareConcurrency || 4;

	let memory = (navigator as unknown as { deviceMemory?: number }).deviceMemory;

	if (!memory) {
		const isMobile =
			/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			) ||
			"ontouchstart" in window ||
			window.screen.width <= 768;

		if (isMobile) {
			if (cores <= 2) {
				memory = 2;
			} else if (cores <= 4) {
				memory = 4;
			} else if (cores <= 6) {
				memory = 6;
			} else {
				memory = 8;
			}
		} else {
			if (cores <= 2) {
				memory = 4;
			} else if (cores <= 4) {
				memory = 8;
			} else if (cores <= 8) {
				memory = 16;
			} else {
				memory = 32;
			}
		}
	}

	const getOptimalThreadCount = (
		performanceMode: "balanced" | "aggressive" = "balanced",
	): number => {
		if (performanceMode === "aggressive") {
			return cores;
		}
		return Math.max(2, Math.floor(cores * 0.75));
	};

	const isUltraHighEnd = cores >= 12 && memory >= 16;
	const isHighEnd = cores >= 8 && memory >= 8;
	const isMidRange = cores >= 4 && memory >= 4;
	const isLowEnd = cores <= 2 || memory <= 2;

	return {
		cores,
		memory,
		isUltraHighEnd,
		isHighEnd,
		isMidRange,
		isLowEnd,
		supportsSharedArrayBuffer,
		balancedThreads: getOptimalThreadCount("balanced"),
		aggressiveThreads: getOptimalThreadCount("aggressive"),
		recommendedPreset: isUltraHighEnd
			? "medium"
			: isHighEnd
				? "fast"
				: isMidRange
					? "faster"
					: "ultrafast",
		detectedArchitecture: architecture,
	};
};

const createFFmpeg = () => {
	const ffmpeg = new FFmpeg();

	ffmpeg.on("log", ({ message }) => {
		if (message && typeof message === 'string') {
			logger.debug({ message }, "[FFmpeg] log");
		}
	});

	return ffmpeg;
};

interface FFmpegProviderProps {
	children: ReactNode;
}

export const FFmpegProvider = ({ children }: FFmpegProviderProps) => {
	const [ffmpegInstance, setFfmpegInstance] = useState<FFmpeg | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hardwareInfo, setHardwareInfo] = useState<HardwareInfo>({ ...defaultHardwareInfo });
	const loadPromiseRef = useRef<Promise<void> | null>(null);

	const loadFFmpeg = useCallback(async () => {
		// Idempotent: if already loaded/loading, return existing promise or resolve
		if (isLoaded || ffmpegInstance) return;
		if (loadPromiseRef.current) return loadPromiseRef.current;

		const promise = (async () => {
			setIsLoading(true);
			setError(null);

			// Register WASM error handlers
			const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
				const reasonStr = event.reason ? String(event.reason) : '';
				if (reasonStr.includes('RuntimeError') || reasonStr.includes('WebAssembly') || reasonStr.includes('indirect call signature mismatch')) {
					logger.error({ reason: event.reason }, "Uncaught WebAssembly error");
					setError('WebAssembly runtime error occurred. This may be due to browser compatibility issues. Please try refreshing the page or using a different browser.');
					event.preventDefault();
				}
			};

			const handleError = (event: ErrorEvent) => {
				const messageStr = event.message ? String(event.message) : '';
				const errorStr = event.error ? String(event.error) : '';
				if (messageStr.includes('RuntimeError') || messageStr.includes('WebAssembly') ||
					errorStr.includes('RuntimeError') || errorStr.includes('WebAssembly') ||
					messageStr.includes('indirect call signature mismatch')) {
					logger.error({ error: event.error }, "Uncaught error");
					setError('WebAssembly error occurred. This may be due to browser compatibility issues. Please try refreshing the page or using a different browser.');
				}
			};

			window.addEventListener('unhandledrejection', handleUnhandledRejection);
			window.addEventListener('error', handleError);

			try {
				// Run hardware detection first
				let detectedHardware = { ...defaultHardwareInfo };
				try {
					detectedHardware = await detectHardwareCapabilities();
					setHardwareInfo(detectedHardware);
				} catch (hwError) {
					logger.error({ error: hwError }, "Hardware detection failed");
				}

				logger.debug("Loading FFmpeg WebAssembly core...");
				const ffmpeg = createFFmpeg();

				const currentArchitecture = detectedHardware.detectedArchitecture || 'x86_64';

				const isDeployment = window.location.hostname.includes('pages.dev') ||
									window.location.hostname.includes('netlify.app') ||
									window.location.hostname.includes('vercel.app') ||
									(!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1'));

				const shouldUseSingleThreaded =
					detectedHardware.isLowEnd ||
					!detectedHardware.supportsSharedArrayBuffer ||
					(isDeployment && !window.crossOriginIsolated);

				let loadConfig;

				logger.debug({ mode: shouldUseSingleThreaded ? 'single-threaded' : 'multi-threaded' }, "Loading FFmpeg");
				logger.debug({ architecture: currentArchitecture, sharedArrayBuffer: detectedHardware.supportsSharedArrayBuffer, crossOriginIsolated: window.crossOriginIsolated }, "FFmpeg environment info");

				const buildConfigs = [];

				if (shouldUseSingleThreaded) {
					buildConfigs.push(
						{
							name: "single-threaded v0.12.6",
							baseURL: "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd",
							config: async (baseURL: string) => ({
								coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
								wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
							})
						},
						{
							name: "single-threaded v0.12.4 (fallback)",
							baseURL: "https://unpkg.com/@ffmpeg/core@0.12.4/dist/umd",
							config: async (baseURL: string) => ({
								coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
								wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
							})
						}
					);
				} else {
					buildConfigs.push(
						{
							name: "multi-threaded v0.12.6",
							baseURL: "https://unpkg.com/@ffmpeg/core-mt@0.12.6/dist/umd",
							config: async (baseURL: string) => ({
								coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
								wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
								workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, "text/javascript"),
							})
						},
						{
							name: "single-threaded v0.12.6 (fallback)",
							baseURL: "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd",
							config: async (baseURL: string) => ({
								coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
								wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
							})
						},
						{
							name: "single-threaded v0.12.4 (compatibility fallback)",
							baseURL: "https://unpkg.com/@ffmpeg/core@0.12.4/dist/umd",
							config: async (baseURL: string) => ({
								coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
								wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
							})
						}
					);
				}

				let loadSuccess = false;
				let lastError: Error | null = null;

				for (const buildConfig of buildConfigs) {
					try {
						logger.debug({ build: buildConfig.name }, "Attempting to load FFmpeg build");
						loadConfig = await buildConfig.config(buildConfig.baseURL);

						await ffmpeg.load(loadConfig);
						logger.debug({ build: buildConfig.name }, "Successfully loaded FFmpeg build");
						loadSuccess = true;
						break;
					} catch (buildError) {
						logger.warn({ build: buildConfig.name, error: buildError }, "Failed to load FFmpeg build");
						lastError = buildError instanceof Error ? buildError : new Error(String(buildError));
						continue;
					}
				}

				if (!loadSuccess) {
					throw lastError || new Error("All FFmpeg build configurations failed to load");
				}

				setFfmpegInstance(ffmpeg);
				setIsLoaded(true);
				logger.debug("FFmpeg loaded successfully!");
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				setError(
					`Error loading FFmpeg: ${errorMessage}. Try refreshing the page.`,
				);
				logger.error({ error }, "FFmpeg Load Error");
				// Reset so it can be retried
				loadPromiseRef.current = null;
			} finally {
				setIsLoading(false);
			}
		})();

		loadPromiseRef.current = promise;
		return promise;
	}, [isLoaded, ffmpegInstance]);

	// Primary method: Use ffprobe-wasm for comprehensive metadata analysis
	const analyzeWithFFprobe = async (file: File): Promise<any> => {
		await ensureFFprobeLoaded();
		if (!FFprobeWorker) {
			throw new Error("ffprobe-wasm is not loaded");
		}

		try {
			const worker = new FFprobeWorker();
			const fileInfo = await worker.getFileInfo(file);
			return fileInfo;
		} catch (error) {
			throw new Error(`ffprobe-wasm analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
		}
	};

	// Frame-level analysis using ffprobe-wasm
	const getFrameAnalysis = async (file: File): Promise<any> => {
		await ensureFFprobeLoaded();
		if (!FFprobeWorker) {
			throw new Error("ffprobe-wasm is not loaded");
		}

		try {
			const worker = new FFprobeWorker();
			const framesInfo = await worker.getFrames(file, 0);
			return framesInfo;
		} catch (error) {
			throw new Error(`Frame analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
		}
	};

	// Combined method that tries ffprobe-wasm first, then falls back to FFmpeg
	const getFileMetadata = async (file: File, inputFileName?: string): Promise<any> => {
		// Try ffprobe-wasm first
		try {
			await ensureFFprobeLoaded();
			if (FFprobeWorker && hardwareInfo.supportsSharedArrayBuffer) {
				logger.debug("Using ffprobe-wasm for metadata extraction");
				return await analyzeWithFFprobe(file);
			}
		} catch (error) {
			logger.warn({ error }, "ffprobe-wasm failed, falling back to FFmpeg method");
		}

		// Fallback to FFmpeg method
		if (!inputFileName) {
			throw new Error("Input filename required for FFmpeg fallback method");
		}

		logger.debug("Using FFmpeg fallback method for metadata extraction");
		return await extractMetadata(inputFileName);
	};

	// Fallback method: Metadata extraction using FFmpeg (since ffprobe is not available in ffmpeg.wasm)
	const extractMetadata = async (inputFile: string): Promise<any> => {
		if (!ffmpegInstance || !isLoaded) {
			throw new Error("FFmpeg is not loaded");
		}

		const metadataFile = `metadata_${Date.now()}.txt`;

		try {
			const metadataArgs = [
				"-hide_banner",
				"-i", inputFile,
				"-f", "ffmetadata",
				metadataFile
			];

			await ffmpegInstance.exec(metadataArgs);

			const metadataData = await ffmpegInstance.readFile(metadataFile);
			const metadataText = new TextDecoder().decode(metadataData as Uint8Array);

			await ffmpegInstance.deleteFile(metadataFile);

			const metadata = parseFFmetadata(metadataText);
			return metadata;
		} catch (error) {
			try {
				await ffmpegInstance.deleteFile(metadataFile);
			} catch {
				// Ignore cleanup errors
			}
			throw error;
		}
	};

	const parseFFmetadata = (metadataText: string) => {
		const lines = metadataText.split('\n');
		const metadata: Record<string, string> = {};

		for (const line of lines) {
			const trimmedLine = line.trim();
			if (trimmedLine && !trimmedLine.startsWith(';') && !trimmedLine.startsWith('#')) {
				const equalIndex = trimmedLine.indexOf('=');
				if (equalIndex > 0) {
					const key = trimmedLine.substring(0, equalIndex).trim();
					const value = trimmedLine.substring(equalIndex + 1).trim();
					metadata[key] = value;
				}
			}
		}

		return metadata;
	};

	const value: FFmpegContextValue = {
		ffmpegInstance,
		isLoaded,
		isLoading,
		hardwareInfo,
		error,
		loadFFmpeg,
		analyzeWithFFprobe,
		extractMetadata,
		getFileMetadata,
		getFrameAnalysis,
	};

	return (
		<FFmpegContext.Provider value={value}>{children}</FFmpegContext.Provider>
	);
};
